import React, { useEffect } from 'react';
import { Trophy, TrendingUp, TrendingDown, Minus, Zap, X, Target } from 'lucide-react';

/**
 * Exercise Complete Modal — Volume-based dopamine loop.
 * Shows when a user completes all sets of one exercise.
 *
 *  Data displayed:
 *   - Current volume: sets × reps × weight
 *   - Delta vs last session (ghost data)
 *   - PR flash if current weight >= historical max
 *   - Adaptive headline based on user.communication_style
 *
 *  NOTE: no vibration.
 */

// Parse reps field which may be "10", "10-12", "12" etc.
function parseRepsNumber(reps) {
  if (reps === null || reps === undefined) return 0;
  const str = String(reps).trim();
  if (!str) return 0;
  // "10-12" → midpoint 11; "10" → 10
  const match = str.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (match) {
    const lo = parseInt(match[1], 10);
    const hi = parseInt(match[2], 10);
    return Math.round((lo + hi) / 2);
  }
  const n = parseInt(str, 10);
  return isNaN(n) ? 0 : n;
}

function parseWeight(w) {
  if (!w) return 0;
  const n = parseFloat(String(w).replace(/[^\d.]/g, ''));
  return isNaN(n) ? 0 : n;
}

function formatVolume(vol) {
  if (vol >= 10000) return `${(vol / 1000).toFixed(1)}k`;
  return vol.toLocaleString();
}

function daysBetween(iso) {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((now - then) / (1000 * 60 * 60 * 24)));
  } catch {
    return null;
  }
}

const TONE_HEADLINES = {
  motivateur: {
    pr: ['RECORD PERSONNEL !', 'NOUVEAU PR !', 'TU EXPLOSES TES LIMITES !'],
    up: ['EN PROGRESSION !', 'TU MONTES !', 'PLUS FORT QU\'AVANT !'],
    equal: ['SOLIDE !', 'BIEN JOUE !', 'CONSTANCE PARFAITE !'],
    down: ['BIEN EXECUTE !', 'TU ES LA !', 'BON TRAVAIL !'],
    noref: ['EXERCICE VALIDE !', 'SERIES COMPLETEES !', 'C\'EST DANS LA BOITE !'],
  },
  empathique: {
    pr: ['Record personnel 🌟', 'Nouveau PR, superbe !', 'Tu te depasses doucement'],
    up: ['Tu progresses !', 'Un pas de plus', 'Belle evolution'],
    equal: ['Stabilite bien tenue', 'Constance respectee', 'Tu tiens le cap'],
    down: ['Seance douce, c\'est ok', 'Ecoute ton corps', 'Validation acquise'],
    noref: ['Exercice complete', 'Bien sur de toi', 'Mission accomplie'],
  },
  commando: {
    pr: ['PR. NOUVEAU MAX ETABLI.', 'RECORD BRISE. PROCHAINE CIBLE.', 'LIMITE DEPASSEE.'],
    up: ['VOLUME EN HAUSSE. CONTINUE.', 'PROGRESSION CONFIRMEE.', 'PLUS LOURD. PLUS FORT.'],
    equal: ['MAINTIEN. ACCEPTABLE.', 'CONSTANT. EXIGE PLUS.', 'STABLE. PROCHAIN PALIER ?'],
    down: ['VOLUME INFERIEUR. ANALYSE.', 'BAISSE NOTEE. RECUPERATION ?', 'EN-DESSOUS. JUSTIFIE.'],
    noref: ['EXERCICE BOUCLE.', 'MISSION EXECUTEE.', 'SERIE TERMINEE.'],
  },
};

const TONE_HEADLINES_EN = {
  motivateur: {
    pr: ['PERSONAL RECORD!', 'NEW PR!', 'YOU SHATTER LIMITS!'],
    up: ['PROGRESSING!', 'GOING UP!', 'STRONGER!'],
    equal: ['SOLID!', 'NICE!', 'CONSISTENCY!'],
    down: ['WELL DONE!', 'YOU SHOWED UP!', 'GOOD WORK!'],
    noref: ['EXERCISE DONE!', 'SETS COMPLETE!', 'LOCKED IN!'],
  },
  empathique: {
    pr: ['Personal best 🌟', 'New PR, great!', 'You\'re surpassing yourself'],
    up: ['Progress made', 'One step up', 'Nice evolution'],
    equal: ['Steady hold', 'Consistency kept', 'On track'],
    down: ['Easy session, it\'s ok', 'Listen to your body', 'Still a win'],
    noref: ['Exercise done', 'Proud of you', 'Mission complete'],
  },
  commando: {
    pr: ['PR. NEW MAX SET.', 'RECORD BROKEN.', 'LIMIT SURPASSED.'],
    up: ['VOLUME UP. CONTINUE.', 'PROGRESS CONFIRMED.', 'HEAVIER. STRONGER.'],
    equal: ['HELD. ACCEPTABLE.', 'STEADY. DEMAND MORE.', 'STABLE. NEXT TIER?'],
    down: ['LOWER VOLUME. ANALYZE.', 'DROP NOTED. RECOVERY?', 'BELOW. JUSTIFY.'],
    noref: ['EXERCISE LOCKED.', 'MISSION EXECUTED.', 'SET DONE.'],
  },
};

export default function ExerciseCompleteModal({
  language = 'fr',
  exerciseName,
  sets,
  reps,
  weight,
  ghost,          // { weight, reps, sets, date } from previous session (may be undefined)
  historyMax,     // historical max weight for this exercise (may be 0)
  tone = 'commando',
  onClose,
}) {
  const fr = language === 'fr';

  // Auto-close after 4s to not block user
  useEffect(() => {
    const id = setTimeout(() => onClose && onClose(), 4200);
    return () => clearTimeout(id);
  }, [onClose]);

  const w = parseWeight(weight);
  const r = parseRepsNumber(reps);
  const s = parseInt(sets, 10) || 0;
  const currentVolume = s * r * w;

  const ghostW = parseWeight(ghost?.weight);
  const ghostR = parseRepsNumber(ghost?.reps);
  const ghostS = parseInt(ghost?.sets, 10) || 0;
  const lastVolume = ghostS * ghostR * ghostW;
  const hasReference = lastVolume > 0;

  const deltaPct = hasReference ? Math.round(((currentVolume - lastVolume) / lastVolume) * 100) : null;
  const isPR = w > 0 && historyMax > 0 && w >= historyMax;

  // Decide trend bucket
  let bucket = 'noref';
  if (isPR) bucket = 'pr';
  else if (hasReference) {
    if (deltaPct > 2) bucket = 'up';
    else if (deltaPct < -2) bucket = 'down';
    else bucket = 'equal';
  }

  const headlinesMap = fr ? TONE_HEADLINES : TONE_HEADLINES_EN;
  const headlinesArr = (headlinesMap[tone] || headlinesMap.commando)[bucket];
  const headline = headlinesArr[Math.floor(Math.random() * headlinesArr.length)];

  const borderClass = isPR
    ? 'border-amber-400/70 shadow-[0_0_60px_rgba(251,191,36,0.35)]'
    : bucket === 'up'
      ? 'border-primary/60 shadow-[0_0_50px_rgba(var(--primary-rgb),0.25)]'
      : bucket === 'down'
        ? 'border-blue-500/40 shadow-[0_0_30px_rgba(59,130,246,0.15)]'
        : 'border-primary/40 shadow-[0_0_30px_rgba(var(--primary-rgb),0.15)]';

  const TrendIcon = isPR ? Trophy : bucket === 'up' ? TrendingUp : bucket === 'down' ? TrendingDown : bucket === 'equal' ? Minus : Target;
  const accentClass = isPR
    ? 'text-amber-400'
    : bucket === 'up'
      ? 'text-primary'
      : bucket === 'down'
        ? 'text-blue-400'
        : 'text-primary';

  const days = ghost?.date ? daysBetween(ghost.date) : null;

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center p-4 animate-in fade-in duration-200"
      data-testid="exercise-complete-modal"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className={`relative w-full max-w-sm bg-card rounded-2xl overflow-hidden border-2 ${borderClass} animate-in zoom-in-95 fade-in duration-300`}
        onClick={(e) => e.stopPropagation()}
        data-testid="ex-complete-card"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-black/60 transition-colors z-10"
          data-testid="ex-complete-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* PR flash banner */}
        {isPR && (
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 animate-pulse" />
        )}

        <div className="px-6 py-6 space-y-4">
          {/* Headline + icon */}
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-xl bg-secondary/50 flex items-center justify-center shrink-0 ${isPR ? 'ring-2 ring-amber-400/50' : ''}`}>
              <TrendIcon className={`w-5 h-5 ${accentClass}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`font-heading text-xs uppercase tracking-[0.2em] ${accentClass} leading-tight`}
                data-testid="ex-complete-headline"
              >
                {headline}
              </p>
              <p className="text-sm font-semibold text-foreground mt-1 truncate" title={exerciseName}>
                {exerciseName}
              </p>
            </div>
          </div>

          {/* Volume display */}
          <div className="bg-secondary/40 rounded-xl p-4 text-center">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground/70 mb-1">
              {fr ? 'Volume total' : 'Total volume'}
            </p>
            <div className="flex items-baseline justify-center gap-2">
              <p className={`font-heading text-4xl font-black tabular-nums ${accentClass}`} data-testid="ex-complete-volume">
                {currentVolume > 0 ? formatVolume(currentVolume) : '—'}
              </p>
              {currentVolume > 0 && (
                <span className="text-xs text-muted-foreground font-medium">lbs</span>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground/80 mt-1 tabular-nums">
              {s > 0 && r > 0 ? `${s} × ${reps} ${w > 0 ? `× ${w} lbs` : ''}` : '—'}
            </p>
          </div>

          {/* Delta badge */}
          {hasReference && (
            <div className="flex items-center justify-center gap-2 text-xs" data-testid="ex-complete-delta">
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold ${
                deltaPct > 2
                  ? 'bg-primary/15 text-primary'
                  : deltaPct < -2
                    ? 'bg-blue-500/15 text-blue-400'
                    : 'bg-muted-foreground/15 text-muted-foreground'
              }`}>
                {deltaPct > 0 ? '+' : ''}{deltaPct}%
              </span>
              <span className="text-muted-foreground">
                {fr
                  ? days !== null && days > 0
                    ? `vs il y a ${days}j`
                    : 'vs derniere séance'
                  : days !== null && days > 0
                    ? `vs ${days}d ago`
                    : 'vs last session'}
              </span>
            </div>
          )}

          {isPR && (
            <div className="flex items-center justify-center gap-1.5 text-xs font-semibold text-amber-400" data-testid="ex-complete-pr-badge">
              <Zap className="w-3.5 h-3.5" />
              <span className="uppercase tracking-wider">{fr ? 'Record battu' : 'Record broken'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
